import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from "react";
import PayCustomerOrderPaymentMethodSelectionSection from "./sections/payment-method-selection";
import {AccountingPaymentOptions} from "../../../../core/constants/enums";
import PayCustomerOrderOnlinePaymentSection from "./sections/online-payment";
import useIsMounted from "../../../hooks/use-is-mounted";
import PayCustomerOrderOtherPaymentsSection from "./sections/other-payments";
import {createUUId} from "../../../../core/services/utils/utils";
import PayCustomerOrderSendEmailSection from "./sections/send-email";
import PayCustomerOrderOnlinePaymentCardSelectionSection from "./sections/online-payment-card-selection";
import LoadingLottieAnimation from "../lottie-animations/loading";
import Api from "../../../../core/services/api/api";

export const PayCustomerOrderSteps = {
    methodSelection: 'methodSelection',
    onlinePayment: 'onlinePayment',
    onlinePaymentCardSelection: 'onlinePaymentCardSelection',
    sendEmail: 'sendEmail',
    otherPayments: 'otherPayments',

}

const PayCustomerOrder = forwardRef(({
                                         showBackButton = false,
                                         exit: exitProps,
                                         pay: payProps,
                                         step = PayCustomerOrderSteps.methodSelection,
                                         setStep,
                                         customerId,
                                         remainingBalance = 0,
                                         minChargeAmount = 0,
                                         showPreAuthMargin = false,
                                         submittingAction,
                                         setSubmittingAction,
                                     }, ref) => {
    const [creditCardProviders, setCreditCardProviders] = useState([]);
    const [loadingProviders, setLoadingProviders] = useState(true);
    const [selectedCreditCard, setSelectedCreditCard] = useState();
    const [sendEmailState, setSendEmailState] = useState({});
    const [processTerminator, setProcessTerminator] = useState(null);
    const isMounted = useIsMounted();

    const onlinePaymentFormIdRef = useRef(createUUId(true));
    const onlinePaymentSubmitterRef = useRef();
    const otherPaymentsFormIdRef = useRef(createUUId(true));
    const otherPaymentSubmitterRef = useRef();
    const sendEmailFormIdRef = useRef(createUUId(true));
    const sendEmailSubmitterRef = useRef();

    /**
     * Fetches the list of credit card providers from the server.
     * @type {(function(): Promise<void>)}
     */
    const getCreditCardProviders = useCallback(async () => {
        setLoadingProviders(true);
        const response = await Api.getPaymentProviders();
        if (!isMounted())
            return;
        setLoadingProviders(false);
        if (response?.resultFlag) {
            const data = response?.data ?? [];
            setCreditCardProviders(data);
        }
    }, [isMounted])

    /**
     * As soon as the component is mounted, fetches the list of credit card providers
     */
    useEffect(() => {
        void getCreditCardProviders();
    }, [getCreditCardProviders])


    /**
     * Exposes a pay method through the given ref of this component.
     * - this method should be used in the parent component when it wants one of its buttons to perform the pay /
     * send-email functionality for this component.
     */
    useImperativeHandle(ref, () => ({
        executeAction: () => {
            switch (step) {
                case PayCustomerOrderSteps.onlinePayment:
                    if (submittingAction)
                        return;
                    return onlinePaymentSubmitterRef.current?.click();
                case PayCustomerOrderSteps.otherPayments:
                    if (submittingAction)
                        return;
                    return otherPaymentSubmitterRef.current?.click();
                case PayCustomerOrderSteps.sendEmail:
                    if (submittingAction)
                        return;
                    return sendEmailSubmitterRef.current?.click();
                default:
                    break;
            }
        },
    }), [
        step,
        onlinePaymentSubmitterRef,
        otherPaymentSubmitterRef,
        submittingAction
    ])

    /**
     * Exits this component.
     *
     * - if processTerminator is set, calls the callback prior to existing.
     * @type {function(): Promise<void>}
     */
    const exit = useCallback(async () => {
        if (processTerminator) {
            await processTerminator();
        }
        exitProps()
        if (!isMounted())
            return;
        setProcessTerminator(null);
    }, [exitProps, isMounted, processTerminator])

    /**
     * calls the [pay] callback form the props.
     *
     * - if the callback's response is successful, and we are in [onlinePayment] step, then navigates the user to
     * send-email step.
     * @type {(function(*): Promise<void>)}
     */
    const pay = useCallback(async (forApi) => {
        setSubmittingAction(true);
        const [resultFlag, emailState, afterEmailSent] = await payProps(forApi);
        if (!isMounted())
            return;
        setSubmittingAction(false);
        if (!resultFlag)
            return;
        switch (step) {
            case PayCustomerOrderSteps.onlinePayment:
                if (!emailState) {
                    setStep(PayCustomerOrderSteps.methodSelection);
                    exit().then();
                    break;
                }
                // only set the step to sendEmail if the emailState is given
                setStep(PayCustomerOrderSteps.sendEmail);
                setSendEmailState(emailState);

                setProcessTerminator(() => afterEmailSent);
                break;
            case PayCustomerOrderSteps.otherPayments:
                exit().then();
                break;
            default:
                break;
        }
    }, [isMounted, payProps, setStep, step, setSubmittingAction, exit])


    /**
     * Pay With Online Payment Method
     * -----
     * Sets the values needed for the server to pay for an order and then calls the [pay] callback.
     * - if the result of the callback is successful, then navigates the user to the next step.
     * @type {(function(*): Promise<void>)}
     */
    const payUsingOnlinePayment = useCallback(async (values) => {
        let forApi;
        const amount = Number(values?.amount ?? '0');
        if (!selectedCreditCard)
            return;
        forApi = {
            amount: amount,
            cardPaymentInfo: {
                cardId: selectedCreditCard?.id,
                paymentProvider: selectedCreditCard?.providerName,
            },
        }
        await pay(forApi);
    }, [selectedCreditCard, pay])

    /**
     * Pay With Other Payment Methods
     * -----
     * Sets the values needed for the server to pay for an order and then calls the [pay] callback.
     * - if the result of the callback is successful, then navigates the user to the next step.
     */
    const payUsingOtherPayments = useCallback(async (values) => {
        const forApi = {
            amount: isNaN(parseFloat(values.amount)) ? 0 : parseFloat(values.amount),
            otherPaymentInfo: {
                referenceNo: values.reference?.length ? values.reference : null,
                methodTypeId: values?.paymentMethod?.id,
                date: values?.date,
            },
        }
        await pay(forApi);
    }, [pay])

    /**
     * Changes the step of this component to [onlinePaymentCardSelection] or [otherPayments] based on the selected
     * payment method.
     * @param {AccountingPaymentOptions} method
     */
    const onPaymentMethodSelected = useCallback((method) => {
        let _step;
        switch (method) {
            case AccountingPaymentOptions.onlinePayment:
                _step = PayCustomerOrderSteps.onlinePaymentCardSelection
                break
            case AccountingPaymentOptions.otherPayments:
                _step = PayCustomerOrderSteps.otherPayments
                break
            default:
                break;
        }
        if (!_step)
            return;
        setStep(_step);
    }, [setStep])

    /**
     * Sets the selected credit card in the internal state and changes the step to otherPayments.
     * @param card
     */
    const onCreditCardSelected = useCallback((card) => {
        setSelectedCreditCard(card);
        setStep(PayCustomerOrderSteps.onlinePayment);
    }, [setStep])

    return (
        <>
            {
                PayCustomerOrderSteps.methodSelection === step &&
                <PayCustomerOrderPaymentMethodSelectionSection
                    canGoBack={showBackButton}
                    goBack={() => exit()}
                    onSelect={onPaymentMethodSelected}
                />
            }
            {
                PayCustomerOrderSteps.onlinePaymentCardSelection === step &&
                <>
                    {
                        !loadingProviders &&
                        <PayCustomerOrderOnlinePaymentCardSelectionSection
                            canGoBack={showBackButton}
                            goBack={() => setStep(PayCustomerOrderSteps.methodSelection)}
                            customerId={customerId}
                            onCreditCardSelected={onCreditCardSelected}
                            providers={creditCardProviders}
                        />
                    }
                    {
                        loadingProviders &&
                        <LoadingLottieAnimation
                            height={'min(400px, 80%)'}
                            width={'min(400px, 80%)'}
                        />
                    }
                </>
            }
            {
                PayCustomerOrderSteps.onlinePayment === step &&
                <PayCustomerOrderOnlinePaymentSection
                    formId={onlinePaymentFormIdRef.current}
                    pay={payUsingOnlinePayment}
                    selectedCard={selectedCreditCard}
                    remainingBalance={remainingBalance}
                    minChargeAmount={minChargeAmount}
                    showPreAuthMargin={showPreAuthMargin}
                    canGoBack={showBackButton}
                    goBack={() => setStep(PayCustomerOrderSteps.onlinePaymentCardSelection)}
                />
            }
            {
                PayCustomerOrderSteps.otherPayments === step &&
                <PayCustomerOrderOtherPaymentsSection
                    formId={otherPaymentsFormIdRef.current}
                    pay={payUsingOtherPayments}
                    remainingBalance={remainingBalance}
                    minChargeAmount={minChargeAmount}
                />
            }
            {
                PayCustomerOrderSteps.sendEmail === step &&
                <PayCustomerOrderSendEmailSection
                    formId={sendEmailFormIdRef.current}
                    customerId={customerId}
                    state={sendEmailState}
                    onEmailSent={exit}
                    setSubmitting={setSubmittingAction}
                />
            }
            <button
                ref={onlinePaymentSubmitterRef}
                className={'d-none'}
                form={onlinePaymentFormIdRef.current}
                type={'submit'}
            />
            <button
                ref={otherPaymentSubmitterRef}
                className={'d-none'}
                form={otherPaymentsFormIdRef.current}
                type={'submit'}
            />
            <button
                ref={sendEmailSubmitterRef}
                className={'d-none'}
                form={sendEmailFormIdRef.current}
                type={'submit'}
            />
        </>
    )
})

export default PayCustomerOrder;
